'use client';

import React, { useState } from 'react';
import { Copy, Check, ArrowRight } from 'lucide-react';
import type { OrientationBlock, OrientationCopyOption } from './orientationData';

interface OrientationLessonContentProps {
  lessonNumber: number;
  lessonTitle: string;
  lessonSlug: string;
  blocks: OrientationBlock[];
  isLastLesson: boolean;
  onNext: () => void;
  nextLabel: string;
}

// ─── Tool card data ───────────────────────────────────────────────────────────
const AI_TOOLS = [
  {
    name: 'Microsoft Copilot',
    whatItIs:
      'AI built directly into Microsoft 365 — the suite most professional organisations already use. It lives inside Outlook, Teams, Word, Excel, and PowerPoint.',
    whatItDoesBest:
      'The tools you already use, made faster. Summarising email threads. Generating meeting notes. Writing first drafts in Word. Making sense of spreadsheets. Preparing presentation outlines.',
    whoItsFor:
      'Anyone whose working day involves Microsoft tools. If your organisation uses Outlook and Teams, Copilot is the AI you already have access to — or will have very soon.',
    honestNote:
      'Not every organisation has switched it on yet. If yours hasn\'t, that\'s fine — everything you learn here about using AI transfers directly to the tools below. The skills are the same. Only the interface changes.',
  },
  {
    name: 'ChatGPT',
    whatItIs:
      'The one most people have heard of. Made by a company called OpenAI. Available at chatgpt.com. The free version is genuinely capable.',
    whatItDoesBest:
      'Writing, thinking, explaining, drafting, research assistance. Excellent at taking a rough idea and turning it into something polished. Good at explaining complex things in plain English. Very good at helping you think through a problem.',
    whoItsFor:
      'Anyone. It requires no special access, no company rollout, no IT approval. You can use it today. If you don\'t have Copilot yet, start here.',
    honestNote:
      'The free version has some limits on the most advanced features. For most everyday professional tasks, it does everything you need.',
  },
  {
    name: 'Claude',
    whatItIs:
      'Made by a company called Anthropic. Available at claude.ai. Known for being careful, considered, and good with longer or more nuanced material.',
    whatItDoesBest:
      'Long documents. Careful analysis. Nuanced questions where getting the tone exactly right matters. Particularly good at reading something you\'ve written and giving honest, useful feedback on it.',
    whoItsFor:
      'Anyone who wants a slightly more considered response — particularly for anything sensitive, complex, or where precision matters more than speed.',
    honestNote:
      'This is the tool I use most for my own writing and thinking work. It tends toward thoughtful rather than fast. Both are useful. Depends what you need.',
  },
  {
    name: 'Gemini',
    whatItIs:
      'Google\'s AI. Built into Google products and available standalone at gemini.google.com.',
    whatItDoesBest:
      'Works well inside the Google ecosystem — Gmail, Google Docs, Google Calendar. Good at research and general assistance. Integrates naturally if your organisation uses Google Workspace.',
    whoItsFor:
      'Anyone already working primarily in Google tools. If your organisation uses Gmail and Google Docs rather than Outlook and Word, Gemini is the natural equivalent of Copilot.',
    honestNote:
      'If you\'re a Microsoft shop, Gemini is less immediately useful — Copilot does the same job inside the tools you already use. No need to add another tool for its own sake.',
  },
  {
    name: 'Perplexity',
    whatItIs:
      'AI-powered search. Different from the others — it searches the web in real time and shows you exactly where the information came from.',
    whatItDoesBest:
      'Research where you need current, reliable, sourced information. Before an important meeting. When you need to understand something quickly and want to know the source. When you\'d normally reach for Google but want an actual answer rather than a list of links to click through.',
    whoItsFor:
      'Anyone who does research as part of their work — or who just wants better answers to questions than a standard search engine provides.',
    honestNote:
      'This is my first stop for anything where I need current information. I use it before meetings, before decisions, before anything where I want to understand the landscape quickly. It\'s the tool I recommend most often to people who aren\'t sure where to start.',
  },
];

// ─── Individual tool card ─────────────────────────────────────────────────────
function ToolCard({
  tool,
  isLast,
}: {
  tool: (typeof AI_TOOLS)[number];
  isLast: boolean;
}) {
  return (
    <div>
      <div className="py-6">
        <h3
          className="font-display font-semibold text-base mb-5"
          style={{ color: '#1B2A4A', letterSpacing: '0.01em' }}
        >
          {tool.name}
        </h3>
        <div className="space-y-4">
          <div>
            <span
              className="block font-body text-xs uppercase tracking-widest font-semibold mb-1.5"
              style={{ color: '#D4891A', letterSpacing: '0.1em' }}
            >
              What it is
            </span>
            <p className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
              {tool.whatItIs}
            </p>
          </div>
          <div>
            <span
              className="block font-body text-xs uppercase tracking-widest font-semibold mb-1.5"
              style={{ color: '#D4891A', letterSpacing: '0.1em' }}
            >
              What it does best
            </span>
            <p className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
              {tool.whatItDoesBest}
            </p>
          </div>
          <div>
            <span
              className="block font-body text-xs uppercase tracking-widest font-semibold mb-1.5"
              style={{ color: '#D4891A', letterSpacing: '0.1em' }}
            >
              Who it&rsquo;s for
            </span>
            <p className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
              {tool.whoItsFor}
            </p>
          </div>
          <div
            className="rounded-xl p-4 mt-2"
            style={{ backgroundColor: '#F9F7F4', border: '1px solid #E5E0D8' }}
          >
            <p
              className="font-body text-sm italic"
              style={{ color: '#555555', lineHeight: '1.85' }}
            >
              {tool.honestNote}
            </p>
          </div>
        </div>
      </div>
      {!isLast && (
        <div
          className="h-px w-full"
          style={{ backgroundColor: '#D4891A', opacity: 0.35 }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

// ─── Tools block ──────────────────────────────────────────────────────────────
function ToolsBlock() {
  return (
    <div
      className="rounded-2xl p-7 lg:p-10"
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}
    >
      <span
        className="block font-body text-xs uppercase tracking-widest font-semibold mb-2"
        style={{ color: '#D4891A', letterSpacing: '0.12em' }}
      >
        The Five Tools Worth Knowing
      </span>
      <div>
        {AI_TOOLS.map((tool, i) => (
          <ToolCard key={tool.name} tool={tool} isLast={i === AI_TOOLS.length - 1} />
        ))}
      </div>
    </div>
  );
}

// ─── Prompt copy block (only used in O4) ─────────────────────────────────────
function PromptCopyBlock({ content, label }: { content: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <div
      className="rounded-xl p-5 relative"
      style={{ backgroundColor: '#1B2A4A' }}
    >
      <div className="flex items-start gap-3 mb-3">
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em' }}
        >
          {label}
        </span>
      </div>
      <p
        className="font-body text-sm leading-relaxed mb-4 pr-10"
        style={{ color: 'rgba(255,255,255,0.88)', lineHeight: '1.72' }}
      >
        &ldquo;{content}&rdquo;
      </p>
      <div className="flex items-center justify-between">
        <p className="font-body text-xs italic" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Copy this and paste it directly into your AI tool.
        </p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 active:scale-95"
          style={{
            backgroundColor: copied ? 'rgba(45,122,45,0.3)' : 'rgba(255,255,255,0.1)',
            color: copied ? '#7FBF7F' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${copied ? 'rgba(45,122,45,0.4)' : 'rgba(255,255,255,0.15)'}`,
          }}
          aria-label={copied ? 'Copied' : 'Copy prompt'}
        >
          {copied ? (
            <><Check size={13} aria-hidden="true" /> Copied</>
          ) : (
            <><Copy size={13} aria-hidden="true" /> Copy</>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Rich section renderer (heading + paragraphs) ────────────────────────────
function RichSections({
  sections,
  textColor = '#1B2A4A',
  headingColor = '#1B2A4A',
  dividerColor,
}: {
  sections: NonNullable<OrientationBlock['sections']>;
  textColor?: string;
  headingColor?: string;
  dividerColor?: string;
}) {
  return (
    <div className="space-y-6">
      {sections.map((section, si) => (
        <div key={si}>
          {section.dividerBefore && (
            <div
              className="h-px w-full mb-6"
              style={{ backgroundColor: '#D4891A' }}
              aria-hidden="true"
            />
          )}
          {!section.dividerBefore && si > 0 && dividerColor && (
            <div
              className="h-px w-full mb-6"
              style={{ backgroundColor: dividerColor }}
              aria-hidden="true"
            />
          )}
          {section.heading && (
            <h2
              className="font-display font-semibold text-lg mb-4"
              style={{ color: headingColor, lineHeight: '1.35' }}
            >
              {section.heading}
            </h2>
          )}
          {section.paragraphs.length > 0 && (
            <div className="space-y-4">
              {section.paragraphs.map((para, pi) => (
                <p
                  key={pi}
                  className="font-body text-base"
                  style={{ color: textColor, lineHeight: '1.85' }}
                >
                  {para}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Single copy option card (dark navy) ──────────────────────────────────────
function CopyOptionCard({ option }: { option: OrientationCopyOption }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(option.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <div className="space-y-4">
      {/* Option label */}
      <h3
        className="font-display font-semibold text-base"
        style={{ color: '#1B2A4A', lineHeight: '1.35' }}
      >
        {option.label}
      </h3>

      {/* Intro paragraphs */}
      {option.intro.map((para, i) => (
        <p key={i} className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
          {para}
        </p>
      ))}

      {/* Dark navy copy box */}
      <div
        className="rounded-xl p-5 relative"
        style={{ backgroundColor: '#1B2A4A' }}
      >
        <p
          className="font-body text-sm leading-relaxed mb-4 pr-2"
          style={{ color: 'rgba(255,255,255,0.88)', lineHeight: '1.75' }}
        >
          {option.prompt}
        </p>
        <div className="flex items-center justify-between">
          <p className="font-body text-xs italic" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Copy and paste into your AI tool.
          </p>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 active:scale-95"
            style={{
              backgroundColor: copied ? 'rgba(45,122,45,0.3)' : 'rgba(255,255,255,0.1)',
              color: copied ? '#7FBF7F' : 'rgba(255,255,255,0.7)',
              border: `1px solid ${copied ? 'rgba(45,122,45,0.4)' : 'rgba(255,255,255,0.15)'}`,
            }}
            aria-label={copied ? 'Copied' : 'Copy prompt'}
          >
            {copied ? (
              <><Check size={13} aria-hidden="true" /> Copied</>
            ) : (
              <><Copy size={13} aria-hidden="true" /> Copy</>
            )}
          </button>
        </div>
      </div>

      {/* Outro paragraphs */}
      {option.outro?.map((para, i) => (
        <p key={i} className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
          {para}
        </p>
      ))}
    </div>
  );
}

// ─── Copy options block (Try It Now — O4) ─────────────────────────────────────
function CopyOptionsBlock({ block }: { block: OrientationBlock }) {
  const options = block.copyOptions ?? [];
  return (
    <div
      className="rounded-2xl p-7 lg:p-10"
      style={{ backgroundColor: '#F0F7F0', border: '2px solid #C6E0C6' }}
    >
      <span
        className="block font-body text-xs uppercase tracking-widest font-semibold mb-2"
        style={{ color: '#D4891A', letterSpacing: '0.12em' }}
      >
        {block.label ?? 'Try It Now'}
      </span>
      <p className="font-body text-base mb-8" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
        Pick one of these. Choose whichever feels most immediately useful — there is no wrong answer.
      </p>
      <div className="space-y-10">
        {options.map((opt, i) => (
          <div key={i}>
            {i > 0 && (
              <div
                className="h-px w-full mb-10"
                style={{ backgroundColor: '#C6E0C6' }}
                aria-hidden="true"
              />
            )}
            <CopyOptionCard option={opt} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Reflection card (warm cream, no input) ───────────────────────────────────
function ReflectionBlock({ block }: { block: OrientationBlock }) {
  const responses = block.reflectionResponses ?? [];
  return (
    <div
      className="rounded-2xl p-7 lg:p-8"
      style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.2)' }}
    >
      <h3
        className="font-display font-semibold text-base mb-4"
        style={{ color: '#1B2A4A' }}
      >
        How did that feel?
      </h3>
      <p className="font-body text-sm mb-5" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
        Not what came back — how did it feel to try it?
      </p>
      <p className="font-body text-sm mb-4" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
        Most people report one of three things after their first real attempt:
      </p>
      <div className="space-y-3 mb-5">
        {responses.map((r, i) => (
          <p
            key={i}
            className="font-body text-sm italic pl-4"
            style={{
              color: '#1B2A4A',
              lineHeight: '1.85',
              borderLeft: '2px solid rgba(212,137,26,0.4)',
            }}
          >
            {r}
          </p>
        ))}
      </div>
      <p className="font-body text-sm" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
        All three are good answers. All three mean the same thing: you now understand something about using AI that you can only learn by trying it.
      </p>
      <p className="font-body text-sm mt-4 font-medium" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
        That&rsquo;s the whole lesson.
      </p>
      <p className="font-body text-xs mt-5" style={{ color: '#888888' }}>
        When you&rsquo;re ready, the main course is waiting.
      </p>
    </div>
  );
}

// ─── Block renderer ───────────────────────────────────────────────────────────
function renderBlock(block: OrientationBlock, index: number) {
  // Prompt blocks only appear in O4
  if (block.type === 'prompt') {
    return (
      <PromptCopyBlock
        key={index}
        content={block.content ?? ''}
        label="Try This Prompt"
      />
    );
  }

  if (block.type === 'situation') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-8"
        style={{ backgroundColor: '#F0EDE8', border: '1px solid #E5E0D8' }}
      >
        <span
          className="block font-body text-xs uppercase tracking-widest font-semibold mb-5"
          style={{ color: '#D4891A', letterSpacing: '0.12em' }}
        >
          The Situation
        </span>
        {block.sections ? (
          <RichSections
            sections={block.sections}
            textColor="#1B2A4A"
            headingColor="#1B2A4A"
          />
        ) : (
          <p className="font-body text-base leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            {block.content}
          </p>
        )}
      </div>
    );
  }

  // Main content card — white background, multi-section
  if (block.type === 'main') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-10"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}
      >
        {block.sections ? (
          <RichSections
            sections={block.sections}
            textColor="#1B2A4A"
            headingColor="#1B2A4A"
            dividerColor="#E5E0D8"
          />
        ) : (
          <p className="font-body text-base leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            {block.content}
          </p>
        )}
      </div>
    );
  }

  if (block.type === 'anna') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-8"
        style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.2)' }}
      >
        <span
          className="block font-body text-xs uppercase tracking-widest font-semibold mb-5"
          style={{ color: '#D4891A', letterSpacing: '0.12em' }}
        >
          Anna&rsquo;s Note
        </span>
        {block.sections ? (
          <RichSections
            sections={block.sections}
            textColor="#1B2A4A"
            headingColor="#1B2A4A"
          />
        ) : (
          <p className="font-body text-base leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            {block.content}
          </p>
        )}
        <p
          className="font-body text-base font-medium mt-6 italic"
          style={{ color: '#D4891A' }}
        >
          — Anna
        </p>
      </div>
    );
  }

  if (block.type === 'why') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-8"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}
      >
        <span
          className="block font-body text-xs uppercase tracking-widest font-semibold mb-5"
          style={{ color: '#D4891A', letterSpacing: '0.12em' }}
        >
          Why This Matters
        </span>
        {block.sections ? (
          <RichSections sections={block.sections} textColor="#1B2A4A" headingColor="#1B2A4A" />
        ) : (
          <p className="font-body text-base leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            {block.content}
          </p>
        )}
      </div>
    );
  }

  if (block.type === 'tools') {
    return <ToolsBlock key={index} />;
  }

  if (block.type === 'copyoptions') {
    return <CopyOptionsBlock key={index} block={block} />;
  }

  if (block.type === 'reflection') {
    return <ReflectionBlock key={index} block={block} />;
  }

  if (block.type === 'beforewestart') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-10"
        style={{ backgroundColor: '#FDFAF5', border: '1px solid rgba(212,137,26,0.25)' }}
      >
        <span
          className="block font-body text-xs uppercase tracking-widest font-semibold mb-5"
          style={{ color: '#D4891A', letterSpacing: '0.12em' }}
        >
          One practical thing before Module 1
        </span>
        <h2
          className="font-display font-semibold mb-4"
          style={{ fontSize: '1.25rem', color: '#1B2A4A', lineHeight: '1.3' }}
        >
          Let&rsquo;s check what you&rsquo;ve got.
        </h2>
        <div className="h-0.5 w-10 rounded-full mb-6" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
        <div className="space-y-4 mb-6">
          <p className="font-body text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.8' }}>
            Before we dive in, let&rsquo;s make sure Copilot is actually available to you. This sounds obvious. It isn&rsquo;t always.
          </p>
          <p className="font-body text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.8' }}>
            Copilot comes in two versions and a lot of people have one but think they have neither.
          </p>
        </div>
        <div className="flex flex-col gap-4 mb-6">
          <div className="rounded-xl p-5" style={{ backgroundColor: '#F0F4FF', border: '1px solid #D4DBEA' }}>
            <p className="font-body text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: '#1B2A4A', letterSpacing: '0.1em' }}>
              Copilot Free (Personal)
            </p>
            <p className="font-body text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
              The free version available to anyone with a Microsoft account. You&rsquo;ll find it at <span className="font-semibold">copilot.microsoft.com</span> or as a standalone app. It&rsquo;s genuinely useful — but it doesn&rsquo;t sit inside your Outlook, Teams, or Word.
            </p>
          </div>
          <div className="rounded-xl p-5" style={{ backgroundColor: '#EBF5EB', border: '1px solid #C6E0C6' }}>
            <p className="font-body text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: '#2D7A2D', letterSpacing: '0.1em' }}>
              Copilot for Microsoft 365 (Work)
            </p>
            <p className="font-body text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
              The one that lives inside your Microsoft apps — Outlook, Teams, Word, Excel, PowerPoint. This is what most of Module 1 is about. Your company needs to have paid for it and switched it on for you.
            </p>
          </div>
        </div>
        <div className="h-px w-full mb-6" style={{ backgroundColor: 'rgba(212,137,26,0.15)' }} aria-hidden="true" />
        <h3 className="font-display font-semibold mb-4" style={{ fontSize: '1rem', color: '#1B2A4A' }}>
          How to find Copilot in your apps
        </h3>
        <p className="font-body text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: '1.8' }}>
          The Copilot icon looks like a small sparkle or star shape. In Outlook, look in the top right corner of your inbox. In Teams, check the Recap tab after any meeting. In Word, Excel, and PowerPoint, look in the Home ribbon at the top of the screen.
        </p>
        <div className="h-px w-full mb-6" style={{ backgroundColor: 'rgba(212,137,26,0.15)' }} aria-hidden="true" />
        <h3 className="font-display font-semibold mb-3" style={{ fontSize: '1rem', color: '#1B2A4A' }}>
          Can&rsquo;t see it anywhere?
        </h3>
        <p className="font-body text-sm leading-relaxed mb-4" style={{ color: '#374151', lineHeight: '1.8' }}>
          Don&rsquo;t worry — this is common. If you&rsquo;re at work, send a message to your IT helpdesk asking whether Copilot has been enabled for your account. If you&rsquo;re using a personal Microsoft account, go to <span className="font-semibold">copilot.microsoft.com</span> — you can start there for free while you get the work version sorted.
        </p>
        <div className="rounded-xl p-5 mt-2" style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.2)' }}>
          <p className="font-body text-sm italic leading-relaxed mb-3" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            &ldquo;I spent two weeks convinced I didn&rsquo;t have Copilot before someone pointed out it was sitting right there in my Outlook the whole time. I just hadn&rsquo;t noticed the icon. Don&rsquo;t be me. Have a proper look first.&rdquo;
          </p>
          <p className="font-body text-xs font-semibold" style={{ color: '#D4891A' }}>— Anna</p>
        </div>
      </div>
    );
  }

  if (block.type === 'try') {
    return (
      <div
        key={index}
        className="rounded-2xl p-7 lg:p-8"
        style={{ backgroundColor: '#F0F7F0', border: '1px solid #C6E0C6' }}
      >
        {block.sections ? (
          <RichSections
            sections={block.sections}
            textColor="#1B2A4A"
            headingColor="#1B2A4A"
          />
        ) : (
          <p className="font-body text-base leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
            {block.content}
          </p>
        )}
      </div>
    );
  }

  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OrientationLessonContent({
  lessonNumber,
  lessonTitle,
  lessonSlug,
  blocks,
  isLastLesson,
  onNext,
  nextLabel,
}: OrientationLessonContentProps) {
  return (
    <div
      className="flex-1 min-h-screen"
      style={{ backgroundColor: '#F9F7F4' }}
    >
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Progress label */}
        <p
          className="font-body text-xs mb-6"
          style={{ color: '#888888' }}
        >
          Orientation — Lesson {lessonNumber} of 4
        </p>

        {/* Lesson header */}
        <div className="mb-10">
          <span
            className="inline-block font-body text-xs uppercase tracking-widest mb-3 font-semibold"
            style={{ color: '#D4891A', letterSpacing: '0.12em' }}
          >
            {lessonSlug}
          </span>
          <h1
            className="font-display font-semibold mb-4"
            style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              color: '#1B2A4A',
              lineHeight: '1.2',
            }}
          >
            {lessonTitle}
          </h1>
          <div
            className="h-px w-12 rounded-full"
            style={{ backgroundColor: '#D4891A' }}
            aria-hidden="true"
          />
        </div>

        {/* Lesson blocks */}
        {blocks.length > 0 ? (
          <div className="flex flex-col gap-6">
            {blocks.map((block, i) => renderBlock(block, i))}
          </div>
        ) : (
          /* Placeholder when content is blank */
          <div
            className="rounded-2xl p-8 text-center"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}
          >
            <p className="font-body text-sm italic" style={{ color: '#9CA3AF' }}>
              Lesson content coming soon.
            </p>
          </div>
        )}

        {/* Orientation complete acknowledgment (only on O4) */}
        {isLastLesson && (
          <div
            className="mt-10 rounded-2xl p-6"
            style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.2)' }}
          >
            <p
              className="font-body text-sm"
              style={{ color: '#1B2A4A', lineHeight: '1.8' }}
            >
              Orientation complete. You&rsquo;re ready to begin.
            </p>
          </div>
        )}

        {/* Next button */}
        <div className="mt-12 pt-8 border-t" style={{ borderColor: '#E5E0D8' }}>
          <button
            onClick={onNext}
            className="w-full inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-6 py-4 rounded-xl transition-all duration-200 active:scale-95"
            style={{ backgroundColor: '#D4891A', color: 'white' }}
          >
            {nextLabel}
            <ArrowRight size={16} aria-hidden="true" />
          </button>
          {lessonNumber < 4 && (
            <p
              className="font-body text-xs text-center mt-3"
              style={{ color: '#888888' }}
            >
              Lesson {lessonNumber + 1} of 4 in the Orientation — then straight into the main course.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
